// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {PreviewGuildInviteHeader} from '@app/features/auth/flow/InviteHeader';
import type {Guild} from '@app/features/guild/models/Guild';
import styles from '@app/features/invite/components/modals/InviteAcceptModal.module.css';
import GuildMembers from '@app/features/member/state/GuildMembers';
import Presence from '@app/features/presence/state/Presence';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import foodPatternUrl from '@app/media/images/i-like-food.svg';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

interface InviteAcceptModalPreviewProps {
	guild: Guild;
	previewName: string | null | undefined;
	previewIconUrl: string | null;
	hasClearedIcon: boolean;
	previewSplashUrl: string | null;
	hasClearedSplash: boolean;
}
export const InviteAcceptModalPreview = observer(function InviteAcceptModalPreview({
	guild,
	previewName,
	previewIconUrl,
	hasClearedIcon,
	previewSplashUrl,
	hasClearedSplash,
}: InviteAcceptModalPreviewProps) {
	const {t} = useLingui();
	const handleDismiss = useCallback(() => {
		ModalCommands.pop();
	}, []);
	const presenceCount = Presence.getPresenceCount(guild.id);
	const memberCount = GuildMembers.getMemberCount(guild.id);
	const guildFeatures = useMemo(() => {
		return Array.from(guild.features);
	}, [guild.features]);
	const splashUrl = useMemo(() => {
		if (hasClearedSplash) {
			return null;
		}
		if (previewSplashUrl) {
			return previewSplashUrl;
		}
		if (guild.splash) {
			return AvatarUtils.getGuildSplashURL({
				id: guild.id,
				splash: guild.splash,
			});
		}
		return null;
	}, [guild.id, guild.splash, hasClearedSplash, previewSplashUrl]);
	return (
		<Modal.Root size="large" className={styles.root} centered onClose={handleDismiss}>
			<Modal.ScreenReaderLabel text={t`Invite modal preview`} />
			<Modal.InsetCloseButton onClick={handleDismiss} disabled={false} />
			<div className={styles.background} aria-hidden>
				{splashUrl ? (
					<div className={styles.splashImage} style={{backgroundImage: `url(${splashUrl})`}} />
				) : (
					<div className={styles.patternImage} style={{backgroundImage: `url(${foodPatternUrl})`}} />
				)}
			</div>
			<div className={styles.cardHost}>
				<div className={styles.card}>
					<div className={styles.cardInner}>
						<PreviewGuildInviteHeader
							guildId={guild.id}
							guildName={guild.name}
							guildIcon={guild.icon}
							features={guildFeatures}
							presenceCount={presenceCount}
							memberCount={memberCount}
							previewIconUrl={hasClearedIcon ? null : previewIconUrl}
							previewName={previewName}
						/>
						<div className={styles.actions}>
							<Button disabled={true}>
								<Trans>Join Community</Trans>
							</Button>
						</div>
					</div>
				</div>
			</div>
		</Modal.Root>
	);
});
